import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { SadmEntityDisplay } from '@modules/sadm/models/sadm-entity-display';
import { ImageBrokenComponent } from '@modules/shared/image-broken/image-broken.component';
import { Store } from '@ngrx/store';
import { formatSadmEntityType, getMediaPreviewUri } from '@pixacare/domain';
import { FormAnswer } from '@pixacare/forms';
import { capitalize } from '@pixacare/shared';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { selectSadmEntity } from '@shared/store/sadm/sadm.selectors';
import { TuiButton, TuiCell, TuiTitle } from '@taiga-ui/core';
import { TuiSkeleton } from '@taiga-ui/kit';
import { TuiCardLarge } from '@taiga-ui/layout';
import { filter, first, map, Observable, of } from 'rxjs';
import { SadmAnalysisService } from '../../services/sadm-analysis.service';

type AnalysisSummary = {
  title: string;
  subtitle?: string;
  previewUri?: string;
};

// The entity type and number when it has one, followed by the SADM client name.
const toAnalysisSubtitle = (sadmEntity: SadmEntityDisplay): string | undefined => {
  const sadmType = sadmEntity.sadmClient?.sadmType;
  const sadmClientName = sadmEntity.sadmClient?.name;
  const entityType = sadmType ? capitalize(formatSadmEntityType(sadmEntity, sadmType)) : undefined;

  if (!sadmClientName) {
    return entityType;
  }

  return entityType && sadmEntity.patientEntityIndex !== undefined
    ? `${entityType} - ${sadmClientName}`
    : sadmClientName;
};

const toAnalysisSummary = (sadmEntity: SadmEntityDisplay): AnalysisSummary => ({
  title: capitalize(sadmEntity.name),
  subtitle: toAnalysisSubtitle(sadmEntity),
  previewUri: getMediaPreviewUri(sadmEntity.previewMedia),
});

@Component({
  selector: 'pxc-sadm-analysis-input',
  templateUrl: './sadm-analysis-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ImageBrokenComponent,
    TranslocoDirective,
    TuiButton,
    TuiCardLarge,
    TuiCell,
    TuiSkeleton,
    TuiTitle,
  ],
  providers: [SadmAnalysisService],
})
export class SadmAnalysisInputComponent implements OnChanges {
  @Input() clientCode: string;
  @Input() patientId: number;

  @Input() sadmEntityId: number;
  @Output() readonly sadmEntityIdChange = new EventEmitter<number>();

  @Input() analysisFormAnswer: FormAnswer;
  @Output() readonly analysisFormAnswerChange = new EventEmitter<FormAnswer>();

  @Input() sadmEntityDraft: SadmEntityDraft;
  @Output() readonly sadmEntityDraftChange = new EventEmitter<SadmEntityDraft>();

  @Input() protocolFormAnswer: FormAnswer;
  @Output() readonly protocolFormAnswerChange = new EventEmitter<FormAnswer>();

  @Input() formInputs: ModuleResolverInputs;

  readonly isLoading = input(false);

  @Output() readonly outputPictures = new EventEmitter<File[]>();

  summary$: Observable<AnalysisSummary>;
  readonly hasPreviewError = signal(false);

  private readonly sadmAnalysisService = inject(SadmAnalysisService);
  private readonly store = inject(Store);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.sadmEntityId || changes.sadmEntityDraft) {
      this.updateSummary();
    }
  }

  updateSummary(): void {
    this.hasPreviewError.set(false);
    this.summary$ = this.sadmEntityId
      ? this.store.select(selectSadmEntity(this.sadmEntityId)).pipe(filter(Boolean), map(toAnalysisSummary))
      : of({ title: capitalize(this.sadmEntityDraft?.name) });
  }

  addAnalysis(): void {
    this.sadmAnalysisService
      .createAnalysisProtocol({
        clientCode: this.clientCode,
        patientId: this.patientId,
        selectedSadmEntityId: this.sadmEntityId,
        formInputs: this.formInputs,
      })
      .pipe(first())
      .subscribe((analysisOutputs) => this.emitAnalysisOutputs(analysisOutputs));
  }

  editAnalysis(): void {
    this.sadmAnalysisService
      .editAnalysisProtocol({
        sadmEntityId: this.sadmEntityId,
        sadmClientId: this.sadmEntityDraft?.sadmClientId,
        analysisFormAnswer: this.analysisFormAnswer,
        protocolFormAnswer: this.protocolFormAnswer,
        formInputs: this.formInputs,
      })
      .pipe(first())
      .subscribe((analysisOutputs) => this.emitAnalysisOutputs(analysisOutputs));
  }

  // Shared by both flows: the edition one resolves no entity, so it only carries answers and pictures.
  private emitAnalysisOutputs(analysisOutputs: {
    sadmEntityId?: number;
    sadmEntityDraft?: SadmEntityDraft;
    analysisFormAnswer?: FormAnswer;
    protocolFormAnswer?: FormAnswer;
    pictures?: File[];
  }): void {
    if (!analysisOutputs) {
      return;
    }

    if (analysisOutputs.sadmEntityId) {
      this.sadmEntityIdChange.emit(analysisOutputs.sadmEntityId);
    }

    if (analysisOutputs.sadmEntityDraft) {
      this.sadmEntityDraftChange.emit(analysisOutputs.sadmEntityDraft);
    }

    if (analysisOutputs.analysisFormAnswer) {
      this.analysisFormAnswerChange.emit(analysisOutputs.analysisFormAnswer);
    }

    if (analysisOutputs.protocolFormAnswer) {
      this.protocolFormAnswerChange.emit(analysisOutputs.protocolFormAnswer);
    }

    if (analysisOutputs.pictures) {
      this.outputPictures.emit(analysisOutputs.pictures);
    }
  }

  deleteAnalysis(): void {
    if (this.analysisFormAnswer) {
      this.analysisFormAnswerChange.emit(null);
    }
    if (this.sadmEntityId) {
      this.sadmEntityIdChange.emit(null);
    }
    if (this.sadmEntityDraft) {
      this.sadmEntityDraftChange.emit(null);
    }
  }
}
